import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { usePaginationSettings } from 'hooks/usePaginationSettings';
import {
  getAIUsageAgenticWorkflowsBreakdownQuery,
  getAIUsageTeamMembersBreakdownQuery,
} from 'query/billing';
import { toastVariant, useToast } from '__components/ToastProvider';

export type Bucket = 'agentic-workflows' | 'team-members';

const DEFAULT_PAGE_SIZE = 10;

const DEFAULT_PAGINATION_SETTINGS = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
  ordering: '',
  search: '',
};

type Params = {
  bucket: Bucket;
  usageCycle?: string;
  enabled: boolean;
};

export const useUsageBreakdownData = ({
  bucket,
  usageCycle,
  enabled,
}: Params) => {
  const { t } = useTranslation();
  const [showToast] = useToast();
  const pagination = usePaginationSettings(DEFAULT_PAGINATION_SETTINGS);
  const { page, pageSize, search, ordering, setPaginationSettings } =
    pagination;

  // reset page when we switch usage cycle
  useEffect(() => {
    setPaginationSettings((prev) =>
      prev.page === 1 ? prev : { ...prev, page: 1 }
    );
  }, [usageCycle, setPaginationSettings]);

  const params = {
    usage_cycle: usageCycle,
    page,
    page_size: pageSize,
    search: search || undefined,
    sort: ordering || undefined,
  };

  const agenticWorkflowsQuery = useQuery(
    getAIUsageAgenticWorkflowsBreakdownQuery({
      params,
      placeholderData: keepPreviousData,
      enabled: enabled && bucket === 'agentic-workflows',
      throwOnError: false,
    })
  );
  const teamMembersQuery = useQuery(
    getAIUsageTeamMembersBreakdownQuery({
      params,
      placeholderData: keepPreviousData,
      enabled: enabled && bucket === 'team-members',
      throwOnError: false,
    })
  );
  const { data, isLoading, isPending, isError } =
    bucket === 'agentic-workflows' ? agenticWorkflowsQuery : teamMembersQuery;

  // Guards against showing the toast again on every re-render
  const shownErrorRef = useRef(false);
  if (isError && !shownErrorRef.current) {
    shownErrorRef.current = true;
    showToast({
      message: t('Failed to load this data.'),
      variant: toastVariant.FAILURE,
    });
  } else if (!isError) {
    shownErrorRef.current = false;
  }

  return { pagination, data, isLoading, isPending, isError };
};

export type UsageBreakdownData = ReturnType<typeof useUsageBreakdownData>;
